import React, {useCallback, useEffect, useState} from "react";
import {
	ModalBackdrop,
	ModalBackdropClickHandler,
	ModalCloseButton,
	ModalContent,
} from "components/Modals/styled";
import {IconRemove} from "components/Icons";
import {useDispatch, useSelector} from "react-redux";
import styled from "styled-components";
import {PrimaryButton, PureButton} from "components/styled";
import {Exist} from "components/Exist";
import {PlayerDetails, PlayerFixtures, PlayerOverview} from "components/PlayerProfile";
import {
	fetchPlayerStatsRequest,
	playerStatsClear,
	removePlayerFromTeam,
	tradeInPlayer,
	tradeOutPlayer,
} from "modules/actions";
import {closePlayerInfoModal} from "modules/actions/modals";
import {
	getIsTeamLockout,
	getPlayerById,
	getPlayerInfoModalState,
	getPlayerInTeamCheckSelector,
	getSquadById,
	isTeamFullFilled,
} from "modules/selectors";
import {PlayerJersey} from "components";
import {media} from "assets/media";
import {useIsPathIncludes} from "modules/utils/hooks";
import {useNavigate} from "react-router-dom";
import {SALARY_CUP_DELIMITER} from "modules/constants";
import {useTranslation} from "react-i18next";

const TabsWrapper = styled.div`
	display: flex;
	justify-content: space-around;
	align-items: center;
	border-bottom: 1px solid #d8d8d8;
`;

const TabBody = styled.div`
	overflow: auto;
	height: auto;
`;

const TabItem = styled(PureButton)`
	max-width: 120px;
	width: 100%;
	color: #222222;
	font-size: 14px;
	letter-spacing: 0;
	line-height: 17px;
	text-align: center;
	padding-bottom: 6px;
	margin-bottom: -1px;
	border-bottom: 4px solid transparent;

	&.active {
		border-bottom: 4px solid #1eb6ff;
	}
`;

const SModalContent = styled(ModalContent)`
	height: 600px;
	display: flex;
	flex-direction: column;

	> div {
		width: 100%;
	}
`;

enum Tab {
	Overview = "Overview",
	Detail = "Detail",
	Fixtures = "Fixtures",
}

export const PlayerProfileModal: React.FC = () => {
	const dispatch = useDispatch();
	const [activeTab, setActiveTab] = useState(Tab.Overview);
	const {isShow, playerId} = useSelector(getPlayerInfoModalState);

	useEffect(() => {
		if (playerId) {
			dispatch(fetchPlayerStatsRequest(playerId));
		}
	}, [dispatch, playerId]);

	const setTab = (e: React.SyntheticEvent<HTMLButtonElement>) => {
		const tab = e.currentTarget.dataset.tab;

		if (!tab) {
			return;
		}

		setActiveTab(tab as Tab);
	};

	const getIsTabActive = (tab: Tab): boolean => {
		return activeTab === tab;
	};

	const getTabClass = (tab: Tab): string => {
		return getIsTabActive(tab) ? "active" : "";
	};

	const closeModal = useCallback(() => {
		dispatch(playerStatsClear());
		dispatch(closePlayerInfoModal());
		setActiveTab(Tab.Overview);
	}, [dispatch]);

	if (!isShow || !playerId) {
		return null;
	}

	return (
		<ModalBackdrop className="fade">
			<ModalBackdropClickHandler onClick={closeModal} />
			<SModalContent className="no-padding full">
				<ModalCloseButton onClick={closeModal}>
					<IconRemove />
				</ModalCloseButton>

				<PlayerInfoBlock playerId={playerId} closeModal={closeModal} />

				<TabsWrapper>
					<TabItem
						className={getTabClass(Tab.Overview)}
						onClick={setTab}
						data-tab={Tab.Overview}>
						Overview
					</TabItem>
					<TabItem
						className={getTabClass(Tab.Detail)}
						onClick={setTab}
						data-tab={Tab.Detail}>
						Detail
					</TabItem>
					<TabItem
						className={getTabClass(Tab.Fixtures)}
						onClick={setTab}
						data-tab={Tab.Fixtures}>
						Fixture
					</TabItem>
				</TabsWrapper>
				<TabBody>
					<Exist when={getIsTabActive(Tab.Overview)}>
						<PlayerOverview playerId={playerId} />
					</Exist>
					<Exist when={getIsTabActive(Tab.Detail)}>
						<PlayerDetails playerId={playerId} />
					</Exist>
					<Exist when={getIsTabActive(Tab.Fixtures)}>
						<PlayerFixtures playerId={playerId} />
					</Exist>
				</TabBody>
			</SModalContent>
		</ModalBackdrop>
	);
};

const Wrapper = styled.div`
	display: flex;
	justify-content: flex-start;
	gap: 16px;
	height: 180px;

	.jersey {
		box-sizing: border-box;
		height: 100%;
		padding: 30px 45px 12px 45px;

		img {
			height: 100%;
			width: auto;
		}
	}

	.info {
		padding-top: 18px;

		h4,
		h3,
		p {
			margin: 0;
		}

		h4 {
			color: ${({theme}) => theme.text_color};
			font-size: 16px;
			letter-spacing: 0;
			line-height: 19px;
			font-weight: normal;
			margin-bottom: 4px;
		}

		h3 {
			color: ${({theme}) => theme.text_color};
			font-size: 36px;
			font-weight: bold;
			letter-spacing: 0;
			line-height: 36px;
			margin-bottom: 16px;
		}

		p {
			color: ${({theme}) => theme.text_color};
			font-size: 14px;
			letter-spacing: 0;
			line-height: 18px;
		}
	}

	.transfer-button {
		display: flex;
		align-items: flex-end;
		padding-right: 12px;
		height: 100%;

		button {
			margin-bottom: 32px;
		}
	}

	@media screen and ${media.lessThanSM} {
		flex-wrap: wrap;
		height: 200px;
		gap: 6px;

		.jersey {
			max-height: 130px;
			height: unset;
			padding-bottom: 0;
			display: flex;
			justify-content: center;
			align-items: center;
			width: 35%;
			box-sizing: border-box;
			padding-right: 0;
			padding-left: 0;
		}

		.info {
			box-sizing: border-box;
			height: unset;
			width: 60%;

			h4 {
				font-size: 14px;
				line-height: 17px;
			}

			h3 {
				font-size: 20px;
				line-height: 24px;
			}

			p {
				font-size: 12px;
				letter-spacing: 0;
				line-height: 18px;
			}
		}

		.transfer-button {
			height: unset;
			width: 100%;
			padding-right: 0;

			button {
				max-width: 140px;
				width: 100%;
			}
		}
	}
`;

interface IPlayerStats {
	playerId: number;
	closeModal: () => void;
}

const PlayerInfoBlock: React.FC<IPlayerStats> = ({playerId, closeModal}) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const player = useSelector(getPlayerById)(playerId);
	const squad = useSelector(getSquadById)(player.squadId);
	const isLockout = useSelector(getIsTeamLockout);
	const isInTeam = useSelector(getPlayerInTeamCheckSelector)(playerId);
	const isTransferPage = useIsPathIncludes("transfer");
	const {t} = useTranslation();

	const isTransferOutDisabled = player?.locked;
	const canTransferOutPlayer = isLockout && !isTransferOutDisabled;
	const isTeamFull = useSelector(isTeamFullFilled);
	const playerCost = Math.floor(player?.cost / SALARY_CUP_DELIMITER);

	const canBeAdded = !isTeamFull && !isInTeam;

	const transferOutPlayer = useCallback(() => {
		if (!isTransferPage) {
			navigate(`transfers/${playerId}`);
			closeModal();
			return;
		}
		dispatch(tradeOutPlayer(playerId));
		dispatch(removePlayerFromTeam(playerId));
		closeModal();
	}, [isTransferPage, dispatch, navigate, playerId, closeModal]);

	const transferInPlayer = useCallback(() => {
		if (!isTransferPage) {
			navigate(`transfers/${playerId}`);
			closeModal();
			return;
		}
		dispatch(tradeInPlayer(playerId));
		closeModal();
	}, [dispatch, isTransferPage, navigate, playerId, closeModal]);

	return (
		<Wrapper>
			<div className="jersey">
				<PlayerJersey squadId={player.squadId} alt={player.firstName} />
			</div>
			<div className="info">
				<h4>{player.firstName}</h4>
				<h3>{player.lastName}</h3>
				<p>{squad?.name || "-"}</p>
				<p>
					{t("player_profiles.header.price")}: ISK{playerCost}
				</p>
				<p>
					{t("player_profiles.header.pick")}: {player.stats.picked_by || 0}%
				</p>
			</div>
			<div className="transfer-button">
				<Exist when={isLockout}>
					<Exist when={isInTeam}>
						<PrimaryButton disabled={!canTransferOutPlayer} onClick={transferOutPlayer}>
							{t("player_profiles.header.transfer_out")}
						</PrimaryButton>
					</Exist>
					<Exist when={!isInTeam}>
						<PrimaryButton disabled={!canBeAdded} onClick={transferInPlayer}>
							{t("player_profiles.header.transfer_in")}
						</PrimaryButton>
					</Exist>
				</Exist>
			</div>
		</Wrapper>
	);
};
